import { renderMessageText } from '../utils/messageFormatting';
import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useWebNotification } from '../hooks/useWebNotification';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  PaperPlaneRight, 
  Image as ImageIcon, 
  Camera, 
  Microphone,
  Info,
  Smiley,
  DotsThreeVertical,
  Copy,
  Star,
  Trash,
  ShareFat,
  ArrowBendUpLeft,
  X,
  Users,
  PushPin,
  PushPinSlash,
  Eye,
  Link,
  DownloadSimple,
  SpeakerSimpleX,
  SpeakerSimpleHigh,
  CaretDown,
  CaretUp
} from '@phosphor-icons/react';
import EmojiPicker from 'emoji-picker-react';
import VoiceMessage from '../components/VoiceMessage';
import WhatsAppInput from '../components/WhatsAppInput';
import MediaPicker from '../components/MediaPicker';
import ChatImage from '../components/ChatImage';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper: Build media URL for group messages
const buildGroupMediaUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${API.replace(/\/api\/?$/, '')}${url}`;
  if (url.startsWith('/uploads/')) return `${API.replace(/\/api\/?$/, '')}/api${url}`;
  return `${API}${url}`;
};

// Helper: Get image group starting at index
const getGroupImageGroup = (msgs, idx) => {
  const msg = msgs[idx];
  if (!msg.image_url || msg.voice_url) return null;
  if (msg.text && msg.text.trim()) return null;
  // Check not already part of a previous group
  if (idx > 0 && msgs[idx - 1].image_url && !msgs[idx - 1].voice_url && msgs[idx - 1].sender_id === msg.sender_id && !msgs[idx - 1].text?.trim()) return null;
  const group = [msg];
  for (let i = idx + 1; i < msgs.length; i++) {
    if (group.length >= 10) break;
    const nextMsg = msgs[i];
    if (!nextMsg.image_url || nextMsg.voice_url || nextMsg.sender_id !== msg.sender_id) break;
    if (!nextMsg.text?.trim()) {
      group.push(nextMsg);
    } else {
      group.push(nextMsg); // Last with caption
      break;
    }
  }
  return group.length > 1 ? group : null;
};

// Helper: Check if message is part of a group but not the first
const isPartOfGroupImageGroup = (msgs, idx) => {
  const msg = msgs[idx];
  if (!msg.image_url || msg.voice_url || idx === 0) return false;
  for (let i = idx - 1; i >= Math.max(0, idx - 10); i--) {
    const prevMsg = msgs[i];
    if (prevMsg.image_url && !prevMsg.voice_url && prevMsg.sender_id === msg.sender_id && !prevMsg.text?.trim()) {
      return true;
    } else break;
  }
  return false;
};

const parseWithdrawalRequest = (text) => {
  if (!text) return null;
  
  const isPending = text.includes('PENDING');
  const isSuccess = text.includes('SUCCESS') || text.includes('PAID');
  const isCancelled = text.includes('CANCELLED') || text.includes('REJECTED');
  
  if (!isPending && !isSuccess && !isCancelled) return null;

  const lines = text.split('\n');
  const getVal = (patterns) => {
    for (const line of lines) {
      for (const pattern of patterns) {
        const match = line.match(pattern);
        if (match && match[1] !== undefined) {
          return match[1].trim();
        }
      }
    }
    return '';
  };

  const username = getVal([/^\s*User Name:\s*(.*)/i, /^\s*User:\s*(.*)/i]);
  const accountHolderName = getVal([/^\s*A\/c Holder Name:\s*(.*)/i, /^\s*A\/c Name:\s*(.*)/i]);
  const bankAccountNumber = getVal([/^\s*Bank Account No:\s*(.*)/i, /^\s*Account:\s*(.*)/i, /^\s*A\/c No:\s*(.*)/i]);
  const ifscCode = getVal([/^\s*IFSC Code:\s*(.*)/i, /^\s*IFSC:\s*(.*)/i]);
  const bankName = getVal([/^\s*Bank Name:\s*(.*)/i, /^\s*Bank:\s*(.*)/i]);
  const upiId = getVal([/^\s*UPI ID:\s*(.*)/i, /^\s*UPI:\s*(.*)/i]);
  const amount = getVal([/^\s*Amount:\s*(.*)/i]);
  const by = getVal([/^\s*-\s*"By:":?\s*(.*)/i, /^\s*By:\s*(.*)/i]);
  const utr = getVal([/^\s*UTR:\s*(.*)/i]);
  const rawPaidFrom = getVal([/^\s*Paid From:\s*(.*)/i]);
  const paidFrom = (rawPaidFrom && !rawPaidFrom.toLowerCase().includes('iss bank account')) ? rawPaidFrom : '';
  const ref = getVal([/^\s*Ref:\s*(.*)/i, /^\s*Reference:\s*(.*)/i]);

  if (!username && !amount) return null;

  return {
    status: isPending ? 'PENDING' : isSuccess ? 'SUCCESS' : 'CANCELLED',
    username,
    accountHolderName,
    bankAccountNumber,
    ifscCode,
    bankName,
    upiId,
    amount,
    by,
    utr,
    paidFrom,
    ref
  };
};

const GroupChatPage = () => {
  const { groupId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification, playNotificationSound, requestPermission } = useWebNotification();
  
  const [group, setGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [hasMoreOlder, setHasMoreOlder] = useState(true);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  
  // New states for WhatsApp-like features
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageMenu, setShowMessageMenu] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [longPressTimer, setLongPressTimer] = useState(null);
  const [showVoiceRecordModal, setShowVoiceRecordModal] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordingTimer, setRecordingTimer] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null); // For reply feature
  const [showForwardModal, setShowForwardModal] = useState(false); // For forward feature
  const [availableGroups, setAvailableGroups] = useState([]); // For forward
  const [showMediaPicker, setShowMediaPicker] = useState(false); // For WhatsApp input
  
  // Image Preview State
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  
  // NEW: Telegram-style features
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showPinnedBanner, setShowPinnedBanner] = useState(true);
  const [currentPinnedIndex, setCurrentPinnedIndex] = useState(0);
  const [showAllPinnedMessages, setShowAllPinnedMessages] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [reactionTargetMessage, setReactionTargetMessage] = useState(null);
  const [showFullEmojiPicker, setShowFullEmojiPicker] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showViewersModal, setShowViewersModal] = useState(false);
  const [viewersData, setViewersData] = useState({ count: 0, viewers: [] });
  const [showMuteOptions, setShowMuteOptions] = useState(false);
  
  // Payment Modal States
  const [payingMsg, setPayingMsg] = useState(null);
  const [payingCard, setPayingCard] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payUtr, setPayUtr] = useState('');
  const [payPaidFrom, setPayPaidFrom] = useState('');
  const [payReference, setPayReference] = useState('');
  const [submittingPay, setSubmittingPay] = useState(false);
  
  // Quick reaction emojis
  const quickReactions = ['👍', '❤️', '🔥', '😂', '😮'];
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null); // For scroll detection
  const inputRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const inputContainerRef = useRef(null); // For keyboard scroll fix
  const prevMessagesLengthRef = useRef(0); // Track previous message count
  const isUserScrollingRef = useRef(false); // Track if user is scrolling
  const isNearBottomRef = useRef(true); // Track if user is near bottom
  const containerRef = useRef(null);
  const messagesRef = useRef([]);
  const previousScrollHeightRef = useRef(null);
  const isLoadingOlderRef = useRef(false);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  
  // Visual Viewport Keyboard Fix for PWA
  useEffect(() => {
    const handleViewportResize = () => {
      if (containerRef.current && window.visualViewport) {
        const vh = window.visualViewport.height;
        containerRef.current.style.height = `${vh}px`;
      }
    };
    
    if (window.visualViewport) {
      handleViewportResize();
      window.visualViewport.addEventListener('resize', handleViewportResize);
      window.visualViewport.addEventListener('scroll', handleViewportResize);
    }
    
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
        window.visualViewport.removeEventListener('scroll', handleViewportResize);
      }
    };
  }, []);
  
  useEffect(() => {
    if (!user || !groupId) {
      navigate('/groups');
      return;
    }

    fetchGroupDetails();
    fetchMessages();
    fetchPinnedMessages();
    fetchMuteStatus();

    // Start polling for new messages AND pinned messages refresh
    pollingIntervalRef.current = setInterval(() => {
      fetchMessages();
      fetchPinnedMessages(); // Also refresh pinned messages on each poll
    }, 1500);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [groupId, user, navigate]);

  // Fetch pinned messages
  const fetchPinnedMessages = async () => {
    try {
      const response = await axios.get(`${API}/api/groups/${groupId}/pinned-messages`);
      setPinnedMessages(response.data.pinned_messages || []);
    } catch (error) {
      console.error('Failed to fetch pinned messages:', error);
    }
  };

  // Fetch mute status
  const fetchMuteStatus = async () => {
    try {
      const response = await axios.get(`${API}/api/groups/${groupId}/mute-status?user_id=${user.id}`);
      setIsMuted(response.data.is_muted);
    } catch (error) {
      console.error('Failed to fetch mute status:', error);
    }
  };

  // Auto-scroll to bottom - SMART VERSION
  // Only scroll if: initial load, user sent message, OR user is near bottom
  useEffect(() => {
    const currentLength = messages.length;
    const prevLength = prevMessagesLengthRef.current;
    
    // Initial load - always scroll
    if (prevLength === 0 && currentLength > 0) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
      }, 50);
    }
    // New messages came - only scroll if user is near bottom
    else if (currentLength > prevLength && isNearBottomRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
      }, 50);
    }
    // If user is scrolling up, don't auto-scroll
    
    prevMessagesLengthRef.current = currentLength;
  }, [messages]);

  // Detect if user is near bottom of chat
  const handleScroll = useCallback((e) => {
    const container = e.target;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Consider "near bottom" if within 150px of bottom
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    isNearBottomRef.current = distanceFromBottom < 150;
    
    // Show/hide scroll to bottom button
    setShowScrollToBottom(distanceFromBottom > 300);
    if (scrollTop < 80) fetchOlderMessages();
  }, []);

  // Mark messages as read when viewing
  useEffect(() => {
    if (groupId && messages.length > 0 && user?.id) {
      axios.post(`${API}/api/groups/${groupId}/mark-read?user_id=${user.id}`)
        .catch(err => console.error('Mark read failed:', err));
    }
  }, [messages, groupId, user]);

  const fetchGroupDetails = async () => {
    try {
      const response = await axios.get(`${API}/api/groups/${groupId}`);
      setGroup(response.data);
    } catch (error) {
      console.error('Failed to fetch group:', error);
    }
  };

  const fetchMessages = async (before = null) => {
    try {
      const params = { user_id: user.id, limit: 20 };
      if (before) params.before = before;
      const response = await axios.get(
        `${API}/api/groups/${groupId}/messages`,
        { params }
      );
      
      const newMessages = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.messages)
        ? response.data.messages
        : [];
      
      // Check for new messages and show notification
      const prevMessagesLength = messages.length;
      
      if (prevMessagesLength > 0 && newMessages.length > prevMessagesLength) {
        const latestMessage = newMessages[newMessages.length - 1];
        // Notify if message is from someone else (not current user)
        if (latestMessage.sender_id !== user.id) {
          const messagePreview = latestMessage.message_type === 'text' 
            ? latestMessage.message_text 
            : latestMessage.message_type === 'image' 
            ? '📷 Photo' 
            : latestMessage.message_type === 'video'
            ? '🎥 Video'
            : latestMessage.message_type === 'voice'
            ? '🎤 Voice message'
            : 'New message';
          
          showNotification({
            title: `${latestMessage.sender_name || 'Group Member'} - ${group?.name || 'Group Chat'}`,
            body: messagePreview,
            tag: `group-${groupId}`,
            data: {
              groupId: groupId,
              senderId: latestMessage.sender_id,
              messageType: latestMessage.message_type
            }
          });
        }
      }
      
      if (before) {
        setMessages((current) => {
          const existing = new Set(current.map((message) => message.id));
          return [...newMessages.filter((message) => !existing.has(message.id)), ...current];
        });
        if (newMessages.length < 20) setHasMoreOlder(false);
      } else {
        setHasMoreOlder(newMessages.length >= 20);
        setMessages((current) => {
          if (current.length === 0) return newMessages;
          const merged = new Map(current.map((message) => [message.id, message]));
          newMessages.forEach((message) => merged.set(message.id, message));
          return Array.from(merged.values()).sort((a, b) => new Date(a.timestamp || a.created_at) - new Date(b.timestamp || b.created_at));
        });
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOlderMessages = async () => {
    if (isLoadingOlderRef.current || !hasMoreOlder || messagesRef.current.length === 0) return;
    const oldest = messagesRef.current[0];
    const before = oldest.timestamp || oldest.created_at;
    if (!before) return;
    isLoadingOlderRef.current = true;
    setIsLoadingOlder(true);
    if (messagesContainerRef.current) previousScrollHeightRef.current = messagesContainerRef.current.scrollHeight;
    try {
      await fetchMessages(before);
    } finally {
      isLoadingOlderRef.current = false;
      setIsLoadingOlder(false);
    }
  };

  useLayoutEffect(() => {
    if (!isLoadingOlder && previousScrollHeightRef.current !== null && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight - previousScrollHeightRef.current;
      previousScrollHeightRef.current = null;
    }
  }, [isLoadingOlder, messages.length]);

  const scrollToBottom = (instant = false) => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: instant ? 'instant' : 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }
  };

  // ALWAYS scroll to bottom when messages load - multiple attempts for reliability
  useEffect(() => {
    if (messages.length > 0) {
      // Immediate scroll with instant behavior
      scrollToBottom(true);
      
      // Use requestAnimationFrame for after DOM paint
      requestAnimationFrame(() => {
        scrollToBottom(true);
        setTimeout(() => scrollToBottom(true), 50);
        setTimeout(() => scrollToBottom(true), 150);
        setTimeout(() => scrollToBottom(true), 300);
        setTimeout(() => scrollToBottom(true), 500);
      });
    }
  }, [messages.length, groupId]);

  // Handle image preview
  const handleImageClick = useCallback((imageUrl) => {
    setPreviewImageUrl(imageUrl);
    setShowImagePreview(true);
  }, []);

  const closeImagePreview = useCallback(() => {
    setShowImagePreview(false);
    setPreviewImageUrl(null);
  }, []);

  // Handle input focus - Keyboard fix
  // Only scroll if user is near bottom
  const handleInputFocus = useCallback(() => {
    if (isNearBottomRef.current) {
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ 
            behavior: 'smooth',
            block: 'end'
          });
        }
      }, 100);
    }
  }, []);

  const handleSendMessage = async (e, textFromInput = null) => {
    e?.preventDefault();
    
    // Use text from parameter (WhatsAppInput) or from form
    let text;
    if (textFromInput) {
      text = textFromInput;
    } else if (e?.target) {
      const formData = new FormData(e.target);
      text = formData.get('message');
    }

    if (!text?.trim()) return;

    try {
      const messageData = {
        sender_id: user.id,
        text: text.trim(),
        image_url: null,
        voice_url: null
      };

      // Add reply reference if replying to a message
      if (replyingTo) {
        messageData.reply_to = {
          message_id: replyingTo.id,
          text: replyingTo.text || '(Media)',
          sender_name: replyingTo.sender_name
        };
      }

      await axios.post(`${API}/api/groups/${groupId}/send-message`, messageData);

      if (e?.target?.reset) {
        e.target.reset();
      }
      setReplyingTo(null); // Clear reply state
      
      // User sent message - force scroll to bottom
      isNearBottomRef.current = true;
      
      fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  // Handler for WhatsAppInput send message (for Group Chat)
  const handleWhatsAppSendMessage = (text) => {
    handleSendMessage(null, text);
  };

  // Handler for WhatsAppInput media (for Group Chat)
  const handleGroupMediaSelected = async (media) => {
    try {
      await axios.post(`${API}/api/groups/${groupId}/send-message`, {
        sender_id: user.id,
        text: media.caption || null,
        image_url: media.type === 'image' ? media.url : null,
        voice_url: media.type === 'voice' ? media.url : null
      });
      
      isNearBottomRef.current = true;
      fetchMessages();
    } catch (error) {
      console.error('Failed to send media:', error);
      toast.error('Failed to send media');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('media_type', 'image');

    try {
      toast.loading('Uploading image...', { id: 'image-upload' });
      
      const uploadResponse = await axios.post(`${API}/api/upload/media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      await axios.post(`${API}/api/groups/${groupId}/send-message`, {
        sender_id: user.id,
        text: null,
        image_url: uploadResponse.data.url,
        voice_url: null
      });

      toast.success('Image sent!', { id: 'image-upload' });
      fetchMessages();
      
      // Reset file input
      e.target.value = '';
    } catch (error) {
      console.error('Failed to upload image:', error);
      toast.error('Failed to send image', { id: 'image-upload' });
    }
  };

  // ─── Mark as Paid / Cancel withdrawal helpers ──────────────────────────────
  const openPayModal = async (msg) => {
    const card = parseWithdrawalRequest(msg.text);
    if (!card || card.status !== 'PENDING') return;

    if (card.username) {
      try {
        const res = await axios.get(`${API}/api/client-bank-details/by-username/${encodeURIComponent(card.username)}`);
        const bd = res.data?.bank_details || res.data?.client_bank_detail || res.data?.saved_bank_detail;
        if (bd) {
          if (bd.account_holder_name) card.accountHolderName = bd.account_holder_name;
          if (bd.bank_account_number) card.bankAccountNumber = bd.bank_account_number;
          if (bd.ifsc_code) card.ifscCode = bd.ifsc_code;
          if (bd.bank_name) card.bankName = bd.bank_name;
          if (bd.upi_id) card.upiId = bd.upi_id;
        }
      } catch (err) {
        console.error('Failed to fetch latest client bank details for modal:', err);
      }
    }

    setPayingMsg(msg);
    setPayingCard(card);
    setPayUtr('');
    setPayPaidFrom('');
    setPayReference('');
    setShowPayModal(true);
  };

  const handleMarkAsPaid = async () => {
    if (submittingPay) return;
    if (!payUtr.trim()) { toast.error('UTR Number is required'); return; }
    setSubmittingPay(true);
    try {
      const byUser = user?.role === 'super_admin' ? 'Super Admin' : (user?.role?.includes('handler') ? 'Handler' : 'Admin');
      let updatedText = `SUCCESS\nUser Name: ${payingCard.username || ''}\nA/c Holder Name: ${payingCard.accountHolderName || ''}\nBank Account No: ${payingCard.bankAccountNumber || ''}\nIFSC Code: ${payingCard.ifscCode || ''}\nBank Name: ${payingCard.bankName || ''}\nUPI ID: ${payingCard.upiId || ''}\nAmount: ${payingCard.amount || ''}\n- "By:": ${byUser}\nUTR: ${payUtr.trim()}`;
      if (payPaidFrom && payPaidFrom.trim()) {
        updatedText += `\nPaid From: ${payPaidFrom.trim()}`;
      }
      if (payReference && payReference.trim()) {
        updatedText += `\nRef: ${payReference.trim()}`;
      }
      await axios.put(`${API}/api/groups/message/${payingMsg.id}/edit`, {
        text: updatedText,
        utr: payUtr.trim(),
        paid_from: payPaidFrom.trim() || null,
        reference: payReference.trim() || null,
        amount: parseFloat(payingCard.amount) || 0,
        username: payingCard.username,
        paid_to: payingCard.bankName
          ? `${payingCard.bankName} ${payingCard.bankAccountNumber}`
          : payingCard.upiId
      });
      toast.success('✅ Withdrawal marked as paid!');
      setMessages(prev => prev.map(m => m.id === payingMsg.id ? { ...m, text: updatedText } : m));
      setShowPayModal(false);
      setPayingMsg(null); setPayingCard(null);
      setPayUtr(''); setPayPaidFrom(''); setPayReference('');
    } catch (err) {
      console.error(err);
      toast.error('Failed to update request status');
    } finally {
      setSubmittingPay(false);
    }
  };

  const handleCancelRequest = async () => {
    if (submittingPay) return;
    if (!window.confirm('Cancel this withdrawal request?')) return;
    setSubmittingPay(true);
    try {
      const updatedText = `CANCELLED\nUser Name: ${payingCard.username}\nA/c Holder Name: ${payingCard.accountHolderName}\nBank Account No: ${payingCard.bankAccountNumber}\nIFSC Code: ${payingCard.ifscCode}\nBank Name: ${payingCard.bankName}\nUPI ID: ${payingCard.upiId}\nAmount: ${payingCard.amount}\n- "By:": ${payingCard.by}`;
      await axios.put(`${API}/api/groups/message/${payingMsg.id}/edit`, {
        text: updatedText,
        username: payingCard.username,
        amount: parseFloat(payingCard.amount) || 0,
        reference: payReference || 'Details Mismatch'
      });
      toast.success('Request cancelled');
      setMessages(prev => prev.map(m => m.id === payingMsg.id ? { ...m, text: updatedText } : m));
      setShowPayModal(false);
      setPayingMsg(null); setPayingCard(null);
    } catch (err) {
      console.error(err);
      toast.error('Failed to cancel request');
    } finally {
      setSubmittingPay(false);
    }
  };
  // ────────────────────────────────────────────────────────────────────────────

  const startCamera = async () => {
    try {
      console.log('🎥 Starting camera...');
      
      const constraints = { 
        video: { 
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      };
      
      console.log('📸 Requesting camera with constraints:', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      console.log('✅ Camera stream obtained:', stream.getTracks());
      
      // First show the camera modal
      setShowCamera(true);
      
      // Then wait a bit for DOM to render and attach stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            console.log('📹 Video metadata loaded');
            videoRef.current.play().then(() => {
              console.log('▶️ Video playing');
              toast.success('Camera opened');
            }).catch(err => {
              console.error('❌ Video play failed:', err);
              toast.error('Failed to start video');
            });
          };
        } else {
          console.error('❌ Video ref is still null after showing modal');
          toast.error('Video element not ready');
          // Stop the stream if video element is not ready
          stream.getTracks().forEach(track => track.stop());
          setShowCamera(false);
        }
      }, 100);
    } catch (error) {
      console.error('❌ Camera error:', error);
      const errorMsg = error.name === 'NotAllowedError' 
        ? 'Camera permission denied. Please allow camera access.'
        : error.name === 'NotFoundError'
        ? 'No camera found on device'
        : error.name === 'NotReadableError'
        ? 'Camera is already in use by another app'
        : `Camera error: ${error.message}`;
      toast.error(errorMsg);
      setShowCamera(false);
    }
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);

    canvas.toBlob(async (blob) => {
      const formData = new FormData();
      formData.append('file', blob, `photo_${Date.now()}.jpg`);
      formData.append('media_type', 'image');

      try {
        const uploadResponse = await axios.post(`${API}/api/upload/media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        await axios.post(`${API}/api/groups/${groupId}/send-message`, {
          sender_id: user.id,
          text: null,
          image_url: uploadResponse.data.url,
          voice_url: null
        });

        stopCamera();
        await fetchMessages();
        toast.success('Photo sent');
      } catch (error) {
        console.error('Failed to send photo:', error);
        toast.error('Failed to send photo');
      }
    }, 'image/jpeg');
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
    }
    setShowCamera(false);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      audioChunksRef.current = []; // Clear previous data
      mediaRecorderRef.current = new MediaRecorder(stream);

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
          console.log('Audio chunk received:', e.data.size, 'bytes');
        }
      };

      mediaRecorderRef.current.onstop = () => {
        console.log('Recording stopped. Total chunks:', audioChunksRef.current.length);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start(100); // Collect data every 100ms
      setIsRecording(true);
      setShowVoiceRecordModal(true);
      setRecordingDuration(0);
      
      // Start duration timer
      const timer = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      setRecordingTimer(timer);
      
      console.log('Recording started');
      
    } catch (error) {
      console.error('Microphone access denied:', error);
      toast.error('Please allow microphone access');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimer) {
        clearInterval(recordingTimer);
        setRecordingTimer(null);
      }
    }
  };

  const cancelRecording = () => {
    stopRecording();
    audioChunksRef.current = [];
    setShowVoiceRecordModal(false);
    setRecordingDuration(0);
    toast.info('Voice message cancelled');
  };

  const sendVoiceMessage = async () => {
    console.log('Send clicked. Chunks:', audioChunksRef.current.length);
    
    // Wait a moment for final data
    await new Promise(resolve => setTimeout(resolve, 300));
    
    stopRecording();
    
    // Wait for stop to complete
    await new Promise(resolve => setTimeout(resolve, 200));
    
    if (audioChunksRef.current.length === 0) {
      toast.error('No audio recorded. Please try again.');
      setShowVoiceRecordModal(false);
      return;
    }

    try {
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
      
      console.log('Audio blob created:', { 
        size: audioBlob.size, 
        type: audioBlob.type,
        chunks: audioChunksRef.current.length 
      });
      
      if (audioBlob.size === 0) {
        throw new Error('Audio blob is empty');
      }

      const formData = new FormData();
      formData.append('file', audioBlob, `voice_${Date.now()}.webm`);
      formData.append('media_type', 'voice');

      toast.info('Uploading voice message...');

      const uploadResponse = await axios.post(`${API}/api/upload/media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000
      });
      
      console.log('Upload success:', uploadResponse.data);

      await axios.post(`${API}/api/groups/${groupId}/send-message`, {
        sender_id: user.id,
        text: null,
        image_url: null,
        voice_url: uploadResponse.data.url
      });

      await fetchMessages();
      toast.success('Voice message sent!');
    } catch (error) {
      console.error('Failed to send voice:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to send voice message';
      toast.error(errorMsg);
    }
    
    setShowVoiceRecordModal(false);
    setRecordingDuration(0);
    audioChunksRef.current = [];
  };

  // WhatsApp-like message actions
  const handleMessageLongPress = (message) => {
    setSelectedMessage(message);
    setShowMessageMenu(true);
  };

  const handleCopyMessage = () => {
    if (selectedMessage?.text) {
      navigator.clipboard.writeText(selectedMessage.text);
      toast.success('Message copied to clipboard');
      setShowMessageMenu(false);
      setSelectedMessage(null);
    } else {
      toast.error('Cannot copy media messages');
    }
  };

  const handleReplyMessage = () => {
    if (!selectedMessage) return;
    
    setReplyingTo(selectedMessage);
    setShowMessageMenu(false);
    setSelectedMessage(null);
    
    // Focus input
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleForwardMessage = async () => {
    if (!selectedMessage) return;
    
    try {
      // Fetch user's groups
      const response = await axios.get(`${API}/api/groups/user-groups?user_id=${user.id}`);
      const groups = response.data.filter(g => g.id !== groupId); // Exclude current group
      
      setAvailableGroups(groups);
      setShowForwardModal(true);
      setShowMessageMenu(false);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      toast.error('Failed to load groups');
    }
  };

  const forwardToGroup = async (targetGroupId) => {
    try {
      await axios.post(`${API}/api/groups/${targetGroupId}/send-message`, {
        sender_id: user.id,
        text: selectedMessage.text || '📷 Forwarded media',
        image_url: selectedMessage.image_url || null,
        voice_url: selectedMessage.voice_url || null
      }, {
        params: {}
      });
      
      toast.success('Message forwarded');
      setShowForwardModal(false);
      setSelectedMessage(null);
      setAvailableGroups([]);
    } catch (error) {
      console.error('Failed to forward message:', error);
      toast.error('Failed to forward message');
    }
  };

  const handleDeleteMessage = async (deleteForEveryone = false) => {
    if (!selectedMessage) return;

    try {
      const endpoint = deleteForEveryone 
        ? `${API}/api/groups/${groupId}/messages/${selectedMessage.id}/delete-for-all?user_id=${user.id}`
        : `${API}/api/groups/${groupId}/messages/${selectedMessage.id}/delete-for-me?user_id=${user.id}`;
      
      await axios.delete(endpoint);

      toast.success(deleteForEveryone ? 'Message deleted for everyone' : 'Message deleted for you');
      fetchMessages();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete message');
    }

    setShowMessageMenu(false);
    setSelectedMessage(null);
  };

  const handleStarMessage = async () => {
    if (!selectedMessage) return;
    
    try {
      // Check if message is already starred by current user
      const isCurrentlyStarred = (Array.isArray(selectedMessage.starred_by) ? selectedMessage.starred_by : []).includes(user.id);
      const newStarState = !isCurrentlyStarred;
      
      await axios.post(`${API}/api/groups/${groupId}/messages/${selectedMessage.id}/star`, {
        user_id: user.id,
        is_starred: newStarState
      });
      
      toast.success(newStarState ? '⭐ Message starred' : 'Star removed');
      
      // Update local state
      setMessages(prevMessages =>
        prevMessages.map(msg =>
          msg.id === selectedMessage.id
            ? {
                ...msg,
                starred_by: newStarState
                  ? [...(Array.isArray(msg.starred_by) ? msg.starred_by : []), user.id]
                  : (Array.isArray(msg.starred_by) ? msg.starred_by : []).filter(id => id !== user.id)
              }
            : msg
        )
      );
    } catch (error) {
      console.error('Star failed:', error);
      toast.error('Failed to star message');
    }
    
    setShowMessageMenu(false);
    setSelectedMessage(null);
  };

  const onEmojiClick = (emojiObject) => {
    if (inputRef.current) {
      const currentValue = inputRef.current.value || '';
      inputRef.current.value = currentValue + emojiObject.emoji;
    }
    setShowEmojiPicker(false);
  };

  // ==================== NEW TELEGRAM-STYLE FUNCTIONS ====================
  
  // Add reaction to message
  const handleAddReaction = async (messageId, emoji) => {
    try {
      await axios.post(`${API}/api/groups/${groupId}/messages/${messageId}/reaction`, {
        user_id: user.id,
        emoji: emoji
      });
      
      // Update local state
      setMessages(prevMessages =>
        prevMessages.map(msg => {
          if (msg.id === messageId) {
            const reactions = { ...msg.reactions } || {};
            if (reactions[emoji]) {
              if (reactions[emoji].includes(user.id)) {
                reactions[emoji] = reactions[emoji].filter(id => id !== user.id);
                if (reactions[emoji].length === 0) delete reactions[emoji];
              } else {
                reactions[emoji].push(user.id);
              }
            } else {
              reactions[emoji] = [user.id];
            }
            return { ...msg, reactions };
          }
          return msg;
        })
      );
      
      setShowReactionPicker(false);
      setReactionTargetMessage(null);
      setShowFullEmojiPicker(false);
    } catch (error) {
      console.error('Failed to add reaction:', error);
      toast.error('Failed to add reaction');
    }
  };

  // Handle reaction emoji from full picker
  const onReactionEmojiClick = (emojiObject) => {
    if (reactionTargetMessage) {
      handleAddReaction(reactionTargetMessage.id, emojiObject.emoji);
    }
  };

  // Pin/Unpin message (Admin/Handler only)
  const handlePinMessage = async (action = 'pin') => {
    if (!selectedMessage) return;
    
    try {
      await axios.post(`${API}/api/groups/${groupId}/messages/${selectedMessage.id}/pin`, {
        user_id: user.id,
        action: action
      });
      
      toast.success(action === 'pin' ? '📌 Message pinned' : 'Pin removed');
      fetchPinnedMessages();
      fetchMessages();
    } catch (error) {
      console.error('Pin failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to pin message');
    }
    
    setShowMessageMenu(false);
    setSelectedMessage(null);
  };

  // Get view count for message (Admin/Handler only)
  const handleViewMessageViews = async (message) => {
    try {
      const response = await axios.get(`${API}/api/groups/${groupId}/messages/${message.id}/views`);
      setViewersData(response.data);
      setShowViewersModal(true);
    } catch (error) {
      console.error('Failed to get views:', error);
      toast.error('Failed to load view count');
    }
    
    setShowMessageMenu(false);
    setSelectedMessage(null);
  };

  // Mute/Unmute group
  const handleMuteGroup = async (muteUntil = null) => {
    try {
      await axios.post(`${API}/api/groups/${groupId}/mute`, {
        user_id: user.id,
        is_muted: !isMuted,
        mute_until: muteUntil
      });
      
      setIsMuted(!isMuted);
      toast.success(isMuted ? '🔔 Notifications enabled' : '🔕 Notifications muted');
      setShowMuteOptions(false);
    } catch (error) {
      console.error('Mute failed:', error);
      toast.error('Failed to update mute settings');
    }
  };

  // Copy link from message
  const handleCopyLink = () => {
    if (!selectedMessage?.text) return;
    
    // Extract URLs from text
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = selectedMessage.text.match(urlRegex);
    
    if (urls && urls.length > 0) {
      navigator.clipboard.writeText(urls[0]);
      toast.success('Link copied!');
    } else {
      toast.error('No link found in message');
    }
    
    setShowMessageMenu(false);
    setSelectedMessage(null);
  };

  // Download media
  const handleDownloadMedia = async () => {
    if (!selectedMessage) return;
    
    const mediaUrl = selectedMessage.image_url || selectedMessage.voice_url;
    if (!mediaUrl) {
      toast.error('No media to download');
      return;
    }
    
    try {
      let fullUrl;
      if (mediaUrl.startsWith('/api/')) {
        fullUrl = `${API.replace(/\/api\/?$/, '')}${mediaUrl}`;
      } else if (mediaUrl.startsWith('/uploads/')) {
        fullUrl = `${API.replace(/\/api\/?$/, '')}/api${mediaUrl}`;
      } else {
        fullUrl = `${API}${mediaUrl}`;
      }
      
      // Create download link
      const link = document.createElement('a');
      link.href = fullUrl;
      link.download = mediaUrl.split('/').pop();
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download');
    }
    
    setShowMessageMenu(false);
    setSelectedMessage(null);
  };

  // Check if message has link
  const hasLink = (text) => {
    if (!text) return false;
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return urlRegex.test(text);
  };

  // Scroll to pinned message
  const scrollToPinnedMessage = (msgId) => {
    const element = document.getElementById(`msg-${msgId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('bg-wa-hover');
      setTimeout(() => element.classList.remove('bg-wa-hover'), 2000);
    }
  };

  // Navigate pinned messages
  const navigatePinned = (direction) => {
    if (direction === 'next') {
      setCurrentPinnedIndex((prev) => (prev + 1) % pinnedMessages.length);
    } else {
      setCurrentPinnedIndex((prev) => (prev - 1 + pinnedMessages.length) % pinnedMessages.length);
    }
  };

  // Check if user is Admin/Handler
  const isStaff = user?.role === 'super_admin' || user?.role?.includes('handler');

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-[#efeae2]">
        <div className="w-10 h-10 border-4 border-[#075e54] border-t-transparent rounded-full animate-spin mb-3"></div>
        <div className="text-[#075e54] font-medium text-sm">Loading group chat...</div>
      </div>
    );
  }

  return (
    <>
    <div ref={containerRef} className="fixed inset-0 bg-wa-hover overflow-hidden" style={{ height: '100dvh' }}>
      {/* Header - WhatsApp Style */}
      <div className="fixed top-0 left-0 right-0 bg-wa-panel border-b shadow-sm z-50">
        <div className="flex items-center h-14 px-2">
          {/* Back Button */}
          <button 
            onClick={() => {
              const isWithdrawGroup = group?.name?.toLowerCase().includes('withdraw') || group?.group_type === 'withdrawal';
              if (isWithdrawGroup) {
                if (user?.role === 'super_admin' || user?.role === 'admin') {
                  navigate('/admin-dashboard', { state: { activeTab: 'withdrawal' } });
                } else if (user?.role === 'customer') {
                  navigate('/customer-dashboard');
                } else {
                  // Handler
                  navigate('/handler-dashboard', { state: { chatTypeTab: 'withdrawal', bottomTab: 'chats' } });
                }
                return;
              }

              // Navigate based on user role
              if (user?.role === 'customer') {
                navigate('/customer/groups');
              } else {
                navigate('/groups');
              }
            }} 
            className="p-2 hover:bg-wa-hover rounded-full text-wa-text-primary"
          >
            <ArrowLeft size={24} />
          </button>
          
          {/* Group Profile Picture */}
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0 ml-1">
            {group?.profile_pic ? (
              <img
                src={`${API}/uploads/group_profiles/${group.profile_pic}`}
                alt="Group"
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-wa-text-secondary font-semibold text-lg">
                {group?.name?.charAt(0)?.toUpperCase() || 'G'}
              </span>
            )}
          </div>
          
          {/* Group Name and Member Count */}
          <div className="flex-1 ml-3 min-w-0">
            <h2 className="text-base font-semibold text-wa-text-primary truncate">{group?.name || 'Group Chat'}</h2>
            <p className="text-xs text-wa-text-secondary">
              {group?.members?.length || 0} members
            </p>
          </div>

          {/* Dashboard icon only for Handler/Admin */}
          {user?.role !== 'customer' && (
            <button
              onClick={() => navigate(`/groups/${groupId}/dashboard`)}
              className="p-2 hover:bg-wa-hover rounded-full text-wa-text-primary"
            >
              <Info size={24} />
            </button>
          )}
          
          {/* Mute Button for Customers */}
          {user?.role === 'customer' && (
            <button
              onClick={() => handleMuteGroup()}
              className="p-2 hover:bg-wa-hover rounded-full text-wa-text-primary"
              title={isMuted ? 'Unmute' : 'Mute'}
            >
              {isMuted ? (
                <SpeakerSimpleX size={24} />
              ) : (
                <SpeakerSimpleHigh size={24} />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Telegram-Style Pinned Message Bar - Fixed below header */}
      {pinnedMessages.length > 0 && showPinnedBanner && (
        <div className="fixed top-14 left-0 right-0 bg-[#e8f5e9] border-l-4 border-[#25D366] px-3 py-2 flex items-center gap-2 z-40 shadow-sm">
          {/* Pin icon */}
          <PushPin size={18} className="text-[#25D366] flex-shrink-0" weight="fill" />
          
          {/* Pinned message preview - clickable */}
          <div 
            className="flex-1 cursor-pointer min-w-0"
            onClick={() => scrollToPinnedMessage(pinnedMessages[currentPinnedIndex]?.id)}
          >
            <div className="text-xs text-[#128C7E] font-medium truncate">
              {pinnedMessages.length > 1 ? `Pinned #${currentPinnedIndex + 1}` : 'Pinned'}
              <span className="text-wa-text-secondary font-normal ml-1">
                {pinnedMessages[currentPinnedIndex]?.text?.substring(0, 50) || '📷 Media'}
                {(pinnedMessages[currentPinnedIndex]?.text?.length || 0) > 50 ? '...' : ''}
              </span>
            </div>
          </div>
          
          {/* Navigate between pinned messages if multiple */}
          {pinnedMessages.length > 1 && (
            <button 
              onClick={() => setShowAllPinnedMessages(true)}
              className="p-1 text-[#128C7E] hover:bg-[#d4edda] rounded"
              title={`${pinnedMessages.length} pinned`}
            >
              <CaretDown size={16} weight="bold" />
            </button>
          )}
          
          {/* Unpin button - Only for Admin/Handler */}
          {(user?.role === 'admin' || user?.role === 'super_admin' || user?.role?.includes('handler')) && (
            <button 
              onClick={async () => {
                // Direct unpin current pinned message
                const msgToUnpin = pinnedMessages[currentPinnedIndex];
                if (!msgToUnpin) return;
                try {
                  await axios.post(`${API}/api/groups/${groupId}/messages/${msgToUnpin.id}/pin`, {
                    user_id: user.id,
                    action: 'unpin'
                  });
                  toast.success('Pin removed');
                  fetchPinnedMessages();
                  fetchMessages();
                } catch (error) {
                  toast.error('Failed to unpin');
                }
              }}
              className="p-1 text-wa-text-secondary hover:text-red-500 hover:bg-wa-hover rounded"
              title="Unpin message"
            >
              <X size={16} weight="bold" />
            </button>
          )}
        </div>
      )}

      {/* All Pinned Messages Modal */}
      {showAllPinnedMessages && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center">
          <div className="bg-wa-panel w-full max-w-md rounded-t-2xl max-h-[70vh] flex flex-col animate-slide-up">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b bg-[#e8f5e9]">
              <div className="flex items-center gap-2">
                <PushPin size={20} className="text-[#25D366]" weight="fill" />
                <h3 className="font-semibold text-wa-text-primary">{pinnedMessages.length} Pinned Messages</h3>
              </div>
              <button 
                onClick={() => setShowAllPinnedMessages(false)}
                className="p-2 hover:bg-[#d4edda] rounded-full"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Pinned Messages List */}
            <div className="flex-1 overflow-y-auto">
              {pinnedMessages.map((msg, idx) => (
                <div 
                  key={msg.id}
                  className="px-4 py-3 border-b border-wa-border cursor-pointer hover:bg-[#e8f5e9] transition-colors"
                  onClick={() => {
                    scrollToPinnedMessage(msg.id);
                    setShowAllPinnedMessages(false);
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-wa-text-primary">{msg.sender_name}</span>
                    <span className="text-xs text-[#128C7E]">#{idx + 1}</span>
                  </div>
                  <p className="text-sm text-wa-text-secondary line-clamp-2">
                    {msg.text || (msg.image_url ? '📷 Photo' : msg.voice_url ? '🎤 Voice' : 'Media')}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Messages Area - Always starts at header bottom (no extra space) */}
      <div 
        ref={messagesContainerRef}
        className="fixed left-0 right-0 overflow-y-auto p-4 bg-[#E5DDD5]"
        style={{ 
          top: pinnedMessages.length > 0 && showPinnedBanner ? '100px' : '72px', 
          bottom: '70px' 
        }}
        onScroll={handleScroll}
      >
        {messages.length === 0 ? (
          <div className="text-center text-wa-text-secondary mt-10">
            No messages yet. Start the conversation!
          </div>
        ) : (
          messages.filter(msg => {
            if (msg.is_virtual) return true;
            if (msg.text && (msg.text.includes('CLIENT BANK DETAILS') || msg.text.includes('Aapke ID se Coins'))) return false;
            return true;
          }).map((msg, idx, filteredMessages) => {
            const isWithdrawalCard = parseWithdrawalRequest(msg.text) !== null;
            const isOwnMessage = msg.sender_id === user?.id;
            const hasReactions = msg.reactions && Object.keys(msg.reactions).length > 0;
            
            // Skip if part of image group but not first
            if (isPartOfGroupImageGroup(filteredMessages, idx)) return null;
            const imageGroup = getGroupImageGroup(filteredMessages, idx);
            
            return (
              <div
                key={msg.id}
                id={`msg-${msg.id}`}
                className={`flex mb-4 ${isOwnMessage ? 'justify-end' : 'justify-start'} transition-colors duration-300`}
              >
                <div className="relative max-w-[75%] md:max-w-md">
                  {/* Pin indicator */}
                  {msg.is_pinned && (
                    <div className="absolute -top-2 -right-2 z-10">
                      <PushPin size={14} className="text-red-500" weight="fill" />
                    </div>
                  )}
                  
                  <div 
                    className={`${isOwnMessage ? 'bg-wa-hover' : 'bg-wa-panel'} rounded-lg p-2.5 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${msg.is_pinned ? 'ring-1 ring-red-200' : ''}`}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      // Show reaction picker first for customers
                      if (user?.role === 'customer') {
                        setReactionTargetMessage(msg);
                        setShowReactionPicker(true);
                      } else {
                        handleMessageLongPress(msg);
                      }
                    }}
                    onTouchStart={() => {
                      const timer = setTimeout(() => {
                        if (user?.role === 'customer') {
                          setReactionTargetMessage(msg);
                          setShowReactionPicker(true);
                        } else {
                          handleMessageLongPress(msg);
                        }
                      }, 800);
                      setLongPressTimer(timer);
                    }}
                    onTouchEnd={() => {
                      if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        setLongPressTimer(null);
                      }
                    }}
                    onTouchMove={() => {
                      if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        setLongPressTimer(null);
                      }
                    }}
                    onMouseDown={() => {
                      const timer = setTimeout(() => {
                        if (user?.role === 'customer') {
                          setReactionTargetMessage(msg);
                          setShowReactionPicker(true);
                        } else {
                          handleMessageLongPress(msg);
                        }
                      }, 800);
                      setLongPressTimer(timer);
                    }}
                    onMouseUp={() => {
                      if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        setLongPressTimer(null);
                      }
                    }}
                    onMouseLeave={() => {
                      if (longPressTimer) {
                        clearTimeout(longPressTimer);
                        setLongPressTimer(null);
                      }
                    }}
                  >
                  {/* Sender Name - Compact style */}
                  {/* For customers: Show \"DWS Team\" for Admin/Handler messages to keep identities anonymous */}
                  <div className="flex items-center gap-1.5 mb-1">
                    {isWithdrawalCard ? (
                      <>
                        <span className="text-[11px] font-semibold text-[#075E54] dark:text-[#25D366]">
                          You
                        </span>
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-600 text-white font-bold uppercase tracking-wider">
                          {msg.sender_role?.includes('handler') ? 'Handler' : 'Admin'}
                        </span>
                      </>
                    ) : (
                      <>
                        <span className={`text-[11px] font-semibold ${isOwnMessage ? 'text-wa-text-primary' : 'text-blue-600'}`}>
                          {isOwnMessage ? 'You' : 
                            (user?.role === 'customer' && (msg.sender_role === 'super_admin' || msg.sender_role?.includes('handler')))
                              ? 'DWS Team'
                              : msg.sender_name}
                        </span>
                        {/* Role badge - Hide for customers viewing staff messages */}
                        {msg.sender_role && !(user?.role === 'customer' && (msg.sender_role === 'super_admin' || msg.sender_role?.includes('handler'))) && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            msg.sender_role === 'super_admin' ? 'bg-wa-hover text-wa-text-primary' :
                            msg.sender_role.includes('handler') ? 'bg-wa-hover text-wa-text-primary' :
                            'bg-wa-hover text-wa-text-secondary'
                          }`}>
                            {msg.sender_role === 'super_admin' ? 'Admin' : 
                             msg.sender_role.includes('handler') ? 'Handler' : 
                             'Customer'}
                          </span>
                        )}
                        {/* DWS Team badge for customers */}
                        {user?.role === 'customer' && (msg.sender_role === 'super_admin' || msg.sender_role?.includes('handler')) && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-wa-hover text-wa-text-primary">
                            Staff
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {/* Reply Reference - WhatsApp Style */}
                  {msg.reply_to && (
                    <div className={`mb-1.5 pl-2 py-1 border-l-3 rounded ${isOwnMessage ? 'border-green-700 bg-wa-hover/30' : 'border-blue-600 bg-wa-hover/30'} text-xs`}>
                      <div className="font-semibold text-[10px] opacity-70">{msg.reply_to.sender_name}</div>
                      <div className="truncate text-[11px] opacity-60">{msg.reply_to.text || '📷 Media'}</div>
                    </div>
                  )}

                  {/* Image Group (merged grid) */}
                  {imageGroup && imageGroup.length > 1 && (
                    <>
                      <div className="grid grid-cols-2 gap-0.5 rounded-lg overflow-hidden mb-2">
                        {imageGroup.slice(0, 4).map((gMsg, gIdx) => (
                          <div key={gIdx} className="relative cursor-pointer" onClick={() => handleImageClick(buildGroupMediaUrl(gMsg.image_url))}>
                            <ChatImage src={buildGroupMediaUrl(gMsg.image_url)} alt="" className="w-full h-[120px] object-cover" />
                            {gIdx === 3 && imageGroup.length > 4 && (
                              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                <span className="text-white text-2xl font-bold">+{imageGroup.length - 4}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      {(() => {
                        const lastMsg = imageGroup[imageGroup.length - 1];
                        return lastMsg.text?.trim() ? <p className="text-sm mb-1">{lastMsg.text}</p> : null;
                      })()}
                    </>
                  )}

                  {/* Single Image */}
                  {msg.image_url && !imageGroup && (
                    <ChatImage
                      src={buildGroupMediaUrl(msg.image_url)}
                      alt="Shared"
                      className="rounded-lg mb-2 max-w-[250px] max-h-[300px] object-cover cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => handleImageClick(buildGroupMediaUrl(msg.image_url))}
                      onLoad={() => console.log('✅ Image loaded:', msg.image_url)}
                      onError={(e) => {
                        console.error('❌ Image load error:', msg.image_url);
                        e.target.style.display = 'none';
                      }}
                    />
                  )}

                  {/* Voice */}
                  {msg.voice_url && (
                    <VoiceMessage 
                      audioUrl={(() => {
                        // Smart URL handling for voice files
                        if (msg.voice_url.startsWith('/api/')) {
                          return `${API.replace(/\/api\/?$/, '')}${msg.voice_url}`;
                        } else if (msg.voice_url.startsWith('/uploads/')) {
                          return `${API.replace(/\/api\/?$/, '')}/api${msg.voice_url}`;
                        } else {
                          return `${API}${msg.voice_url}`;
                        }
                      })()} 
                    />
                  )}

                  {/* Withdrawal Request Card or Plain Text */}
                  {msg.text && (() => {
                    const card = parseWithdrawalRequest(msg.text);
                    if (card) {
                      const isPending = card.status === 'PENDING';
                      const isSuccess = card.status === 'SUCCESS';
                      const isCancelled = card.status === 'CANCELLED';
                      const canAct = user?.role === 'super_admin' || user?.role?.includes('handler');
                      return (
                        <div 
                          onClick={() => {
                            if (isPending && canAct) {
                              openPayModal(msg);
                            }
                          }}
                          className={`rounded-lg overflow-hidden border-l-4 text-[12px] transition-all ${
                            isPending && canAct ? 'cursor-pointer hover:bg-yellow-100/50 dark:hover:bg-yellow-900/30' : ''
                          } ${
                            isPending ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' :
                            isSuccess ? 'border-green-500 bg-green-50 dark:bg-green-900/20' :
                            'border-red-400 bg-red-50 dark:bg-red-900/20'
                          }`}
                        >
                          {/* Card Header */}
                          <div className={`px-3 py-1.5 flex items-center justify-between ${
                            isPending ? 'bg-yellow-500/20' :
                            isSuccess ? 'bg-green-600' :
                            'bg-red-600'
                          }`}>
                            <span className={`font-bold text-[10px] uppercase tracking-wider ${
                              isPending ? 'text-yellow-700 dark:text-yellow-300' :
                              'text-white'
                            }`}>
                              {isPending ? '⏳ PENDING WITHDRAWAL' : 
                               isSuccess ? `✔ PAID - ${(card.by || 'SYSTEM').toUpperCase()} - UTR ${card.utr || ''}` : 
                               `❌ CANCELLED - ${(card.by || 'SYSTEM').toUpperCase()}`}
                            </span>
                            {isPending && canAct && (
                              <button
                                onClick={(e) => { e.stopPropagation(); openPayModal(msg); }}
                                className="text-[10px] bg-green-600 hover:bg-green-700 text-white px-2 py-0.5 rounded font-semibold transition-colors"
                              >
                                Mark as Paid
                              </button>
                            )}
                          </div>

                          {/* Card Body */}
                          <div className="px-3 py-2 space-y-0.5">
                            {card.username && (
                              <div className="flex gap-2"><span className="text-wa-text-secondary min-w-[80px]">User</span><span className="font-semibold text-wa-text-primary">{card.username}</span></div>
                            )}
                            {card.amount && (
                              <div className="flex gap-2"><span className="text-wa-text-secondary min-w-[80px]">Amount</span><span className="font-bold text-wa-text-primary text-[13px]">₹ {card.amount}</span></div>
                            )}
                            {card.accountHolderName && (
                              <div className="flex gap-2"><span className="text-wa-text-secondary min-w-[80px]">A/c Name</span><span className="font-semibold text-wa-text-primary">{card.accountHolderName}</span></div>
                            )}
                            {card.bankAccountNumber && (
                              <div className="flex gap-2"><span className="text-wa-text-secondary min-w-[80px]">Account</span><span className="font-medium text-wa-text-primary">{card.bankAccountNumber}</span></div>
                            )}
                            {card.ifscCode && (
                              <div className="flex gap-2"><span className="text-wa-text-secondary min-w-[80px]">IFSC</span><span className="font-medium text-wa-text-primary">{card.ifscCode}</span></div>
                            )}
                            {card.bankName && (
                              <div className="flex gap-2"><span className="text-wa-text-secondary min-w-[80px]">Bank</span><span className="font-medium text-wa-text-primary">{card.bankName}</span></div>
                            )}
                            {card.upiId && (
                              <div className="flex gap-2"><span className="text-wa-text-secondary min-w-[80px]">UPI</span><span className="font-medium text-wa-text-primary">{card.upiId}</span></div>
                            )}
                            {/* Success-specific extra info */}
                            {isSuccess && (<>
                              <div className="flex gap-2 mt-1 pt-1 border-t border-green-200 dark:border-green-700">
                                <span className="text-wa-text-secondary min-w-[80px]">UTR</span>
                                <span className="font-bold text-green-700 dark:text-green-300">{card.utr || '-'}</span>
                              </div>
                              {card.paidFrom && card.paidFrom.trim() && (
                                <div className="flex gap-2">
                                  <span className="text-wa-text-secondary min-w-[80px]">Paid From</span>
                                  <span className="font-medium text-wa-text-primary">{card.paidFrom}</span>
                                </div>
                              )}
                              <div className="flex gap-2">
                                <span className="text-wa-text-secondary min-w-[80px]">Ref</span>
                                <span className="font-medium text-wa-text-primary">{card.ref || '-'}</span>
                              </div>
                            </>)}
                          </div>
                        </div>
                      );
                    }
                    // Plain text fallback
                    return (
                      <p className="text-wa-text-primary break-words text-[13px] leading-[1.4] whitespace-pre-line">
                        {renderMessageText(msg.text)}
                      </p>
                    );
                  })()}

                  {/* Timestamp - WhatsApp Style with Star Icon */}
                  <div className="flex items-center justify-end gap-1 mt-1">
                    {(Array.isArray(msg.starred_by) ? msg.starred_by : []).includes(user?.id) && (
                      <span className="text-yellow-500 text-[11px]">⭐</span>
                    )}
                    {/* View count for Admin/Handler */}
                    {isStaff && msg.viewed_by && (
                      <span className="text-[10px] text-wa-text-secondary flex items-center gap-0.5">
                        <Eye size={10} />
                        {msg.viewed_by.length}
                      </span>
                    )}
                    <span className="text-[10px] text-wa-text-secondary">
                      {new Date(msg.timestamp).toLocaleTimeString('en-IN', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </span>
                  </div>
                  </div>
                  
                  {/* Reactions Display - Below message bubble */}
                  {hasReactions && (
                    <div className={`flex flex-wrap gap-1 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                      {Object.entries(msg.reactions).map(([emoji, users]) => (
                        <button
                          key={emoji}
                          onClick={() => handleAddReaction(msg.id, emoji)}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${
                            users.includes(user?.id) 
                              ? 'bg-wa-hover border border-blue-300' 
                              : 'bg-wa-hover border border-wa-border'
                          } hover:bg-wa-hover`}
                        >
                          <span>{emoji}</span>
                          <span className="text-wa-text-secondary">{users.length}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to Bottom Button */}
      {showScrollToBottom && (
        <button
          onClick={scrollToBottom}
          className="fixed bottom-24 right-4 z-40 bg-wa-panel shadow-lg rounded-full p-3 hover:bg-wa-hover transition-colors border"
        >
          <CaretDown size={24} className="text-wa-text-secondary" weight="bold" />
        </button>
      )}

      {/* Reaction Picker Modal (Customer Long Press) */}
      {showReactionPicker && reactionTargetMessage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center"
          onClick={() => {
            setShowReactionPicker(false);
            setReactionTargetMessage(null);
            setShowFullEmojiPicker(false);
          }}
        >
          <div className="bg-wa-panel rounded-2xl p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
            {/* Quick Reactions */}
            <div className="flex gap-2 mb-3">
              {quickReactions.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => handleAddReaction(reactionTargetMessage.id, emoji)}
                  className="text-2xl hover:scale-125 transition-transform p-2"
                >
                  {emoji}
                </button>
              ))}
              <button
                onClick={() => setShowFullEmojiPicker(true)}
                className="text-2xl hover:scale-110 transition-transform p-2 text-wa-text-secondary"
              >
                ➕
              </button>
            </div>
            
            {/* Full Emoji Picker */}
            {showFullEmojiPicker && (
              <div className="mt-2">
                <EmojiPicker onEmojiClick={onReactionEmojiClick} height={300} width={280} />
              </div>
            )}
            
            {/* Additional Options for Customer */}
            <div className="border-t pt-3 space-y-2">
              {/* Copy Text */}
              {reactionTargetMessage.text && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(reactionTargetMessage.text);
                    toast.success('Copied!');
                    setShowReactionPicker(false);
                    setReactionTargetMessage(null);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-wa-hover rounded-lg transition-colors"
                >
                  <Copy size={18} className="text-wa-text-secondary" />
                  <span className="text-sm">Copy Text</span>
                </button>
              )}
              
              {/* Copy Link */}
              {hasLink(reactionTargetMessage.text) && (
                <button
                  onClick={() => {
                    const urlRegex = /(https?:\/\/[^\s]+)/g;
                    const urls = reactionTargetMessage.text.match(urlRegex);
                    if (urls) {
                      navigator.clipboard.writeText(urls[0]);
                      toast.success('Link copied!');
                    }
                    setShowReactionPicker(false);
                    setReactionTargetMessage(null);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-wa-hover rounded-lg transition-colors"
                >
                  <Link size={18} className="text-wa-text-secondary" />
                  <span className="text-sm">Copy Link</span>
                </button>
              )}
              
              {/* Download Media */}
              {(reactionTargetMessage.image_url || reactionTargetMessage.voice_url) && (
                <button
                  onClick={() => {
                    const mediaUrl = reactionTargetMessage.image_url || reactionTargetMessage.voice_url;
                    let fullUrl;
                    if (mediaUrl.startsWith('/api/')) {
                      fullUrl = `${API.replace(/\/api\/?$/, '')}${mediaUrl}`;
                    } else if (mediaUrl.startsWith('/uploads/')) {
                      fullUrl = `${API.replace(/\/api\/?$/, '')}/api${mediaUrl}`;
                    } else {
                      fullUrl = `${API}${mediaUrl}`;
                    }
                    const link = document.createElement('a');
                    link.href = fullUrl;
                    link.download = mediaUrl.split('/').pop();
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    toast.success('Download started');
                    setShowReactionPicker(false);
                    setReactionTargetMessage(null);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-wa-hover rounded-lg transition-colors"
                >
                  <DownloadSimple size={18} className="text-wa-text-secondary" />
                  <span className="text-sm">Download Media</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Message Options Menu (Admin/Handler - WhatsApp-like) */}
      {showMessageMenu && selectedMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center" onClick={() => setShowMessageMenu(false)}>
          <div className="bg-wa-panel rounded-lg p-4 w-80 max-w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            {/* Quick Reactions Row */}
            <div className="flex justify-center gap-2 mb-4 pb-3 border-b">
              {quickReactions.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => handleAddReaction(selectedMessage.id, emoji)}
                  className="text-2xl hover:scale-125 transition-transform p-1"
                >
                  {emoji}
                </button>
              ))}
              <button
                onClick={() => {
                  setReactionTargetMessage(selectedMessage);
                  setShowMessageMenu(false);
                  setShowReactionPicker(true);
                  setShowFullEmojiPicker(true);
                }}
                className="text-xl hover:scale-110 transition-transform p-1 text-wa-text-secondary"
              >
                ➕
              </button>
            </div>
            
            <div className="mb-3 p-3 bg-wa-hover rounded border-l-4 border-green-600">
              <p className="text-sm text-wa-text-primary line-clamp-2">
                {selectedMessage.text || '(Media message)'}
              </p>
            </div>

            <div className="space-y-1">
              {/* Copy */}
              {selectedMessage.text && (
                <button
                  onClick={handleCopyMessage}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-wa-hover rounded-lg transition-colors"
                >
                  <Copy size={20} className="text-wa-text-secondary" />
                  <span className="font-medium">Copy Message</span>
                </button>
              )}

              {/* Copy Link */}
              {hasLink(selectedMessage.text) && (
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-wa-hover rounded-lg transition-colors"
                >
                  <Link size={20} className="text-wa-text-secondary" />
                  <span className="font-medium">Copy Link</span>
                </button>
              )}

              {/* Download Media */}
              {(selectedMessage.image_url || selectedMessage.voice_url) && (
                <button
                  onClick={handleDownloadMedia}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-wa-hover rounded-lg transition-colors"
                >
                  <DownloadSimple size={20} className="text-wa-text-secondary" />
                  <span className="font-medium">Download Media</span>
                </button>
              )}

              {/* Reply */}
              <button
                onClick={handleReplyMessage}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-wa-hover rounded-lg transition-colors"
              >
                <ArrowBendUpLeft size={20} className="text-wa-text-secondary" />
                <span className="font-medium">Reply</span>
              </button>

              {/* Forward */}
              <button
                onClick={handleForwardMessage}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-wa-hover rounded-lg transition-colors"
              >
                <ShareFat size={20} className="text-wa-text-secondary" />
                <span className="font-medium">Forward</span>
              </button>

              {/* Pin/Unpin (Admin/Handler only) */}
              {isStaff && (
                <button
                  onClick={() => handlePinMessage(selectedMessage.is_pinned ? 'unpin' : 'pin')}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-wa-hover rounded-lg transition-colors"
                >
                  {selectedMessage.is_pinned ? (
                    <>
                      <PushPinSlash size={20} className="text-wa-text-secondary" />
                      <span className="font-medium">Unpin Message</span>
                    </>
                  ) : (
                    <>
                      <PushPin size={20} className="text-wa-text-secondary" />
                      <span className="font-medium">Pin Message</span>
                    </>
                  )}
                </button>
              )}

              {/* View Count (Admin/Handler only) */}
              {isStaff && (
                <button
                  onClick={() => handleViewMessageViews(selectedMessage)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-wa-hover rounded-lg transition-colors"
                >
                  <Eye size={20} className="text-wa-text-secondary" />
                  <span className="font-medium">View Message Views</span>
                </button>
              )}

              {/* Star */}
              <button
                onClick={handleStarMessage}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-wa-hover rounded-lg transition-colors"
              >
                <Star size={20} className="text-wa-text-secondary" />
                <span className="font-medium">{(Array.isArray(selectedMessage.starred_by) ? selectedMessage.starred_by : []).includes(user?.id) ? 'Unstar' : 'Star'}</span>
              </button>

              {/* Delete for Me */}
              <button
                onClick={() => handleDeleteMessage(false)}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-wa-hover rounded-lg transition-colors"
              >
                <Trash size={20} className="text-red-600" />
                <span className="font-medium text-red-600">Delete for Me</span>
              </button>

              {/* Delete for Everyone (only for sender or admin/handler) */}
              {(selectedMessage.sender_id === user.id || isStaff) && (
                <button
                  onClick={() => handleDeleteMessage(true)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-wa-hover rounded-lg transition-colors"
                >
                  <Trash size={20} className="text-red-600" weight="fill" />
                  <span className="font-medium text-red-600">Delete for Everyone</span>
                </button>
              )}

              {/* Cancel */}
              <button
                onClick={() => setShowMessageMenu(false)}
                className="w-full px-4 py-3 mt-2 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Voice Recording Modal - WhatsApp Style */}
      {showVoiceRecordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center">
          <div className="bg-wa-panel rounded-2xl p-6 w-80 shadow-2xl">
            <div className="text-center mb-6">
              <div className="relative inline-block">
                <Microphone size={64} className="text-red-600 mx-auto mb-3 animate-pulse" weight="fill" />
                {isRecording && (
                  <div className="absolute -top-2 -right-2 w-4 h-4 bg-red-600 rounded-full animate-ping"></div>
                )}
              </div>
              <p className="text-3xl font-bold text-wa-text-primary mb-1">
                {Math.floor(recordingDuration / 60)}:{(recordingDuration % 60).toString().padStart(2, '0')}
              </p>
              <p className="text-wa-text-secondary">Recording voice message...</p>
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={cancelRecording} 
                className="flex-1 bg-gray-500 text-white py-3 rounded-xl font-semibold hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
              >
                <X size={20} weight="bold" />
                Cancel
              </button>
              <button 
                onClick={sendVoiceMessage} 
                className="flex-1 bg-wa-primary-strong text-white py-3 rounded-xl font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
              >
                <PaperPlaneRight size={20} weight="bold" />
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Camera Modal */}
      {showCamera && (
        <div className="fixed inset-0 bg-black z-[60] flex flex-col">
          <div className="flex-1 relative overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>

          <div className="bg-black bg-opacity-90 p-6 flex items-center justify-around">
            <button
              onClick={stopCamera}
              className="text-white bg-red-600 px-8 py-4 rounded-full font-semibold hover:bg-red-700 transition-colors text-lg"
            >
              Cancel
            </button>
            <button
              onClick={capturePhoto}
              className="bg-wa-panel w-20 h-20 rounded-full flex items-center justify-center border-4 border-white hover:opacity-80 transition-opacity"
            >
              <div className="w-16 h-16 bg-wa-panel rounded-full"></div>
            </button>
          </div>
        </div>
      )}

      {/* Forward Modal */}
      {showForwardModal && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4">
          <div className="bg-wa-panel rounded-lg max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-semibold">Forward to...</h3>
              <button
                onClick={() => {
                  setShowForwardModal(false);
                  setAvailableGroups([]);
                  setSelectedMessage(null);
                }}
                className="text-wa-text-secondary hover:text-wa-text-primary"
              >
                <X size={24} weight="bold" />
              </button>
            </div>

            {/* Groups List */}
            <div className="flex-1 overflow-y-auto p-4">
              {availableGroups.length === 0 ? (
                <div className="text-center text-wa-text-secondary py-8">
                  No other groups available
                </div>
              ) : (
                <div className="space-y-2">
                  {availableGroups.map((group) => (
                    <button
                      key={group.id}
                      onClick={() => forwardToGroup(group.id)}
                      className="w-full flex items-center gap-3 p-3 hover:bg-wa-hover rounded-lg transition-colors text-left"
                    >
                      {group.profile_pic ? (
                        <img
                          src={`${API}/uploads/group_profiles/${group.profile_pic}`}
                          alt={group.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-wa-hover rounded-full flex items-center justify-center">
                          <Users size={24} className="text-green-600" weight="bold" />
                        </div>
                      )}
                      <div className="flex-1">
                        <div className="font-medium">{group.name}</div>
                        <div className="text-sm text-wa-text-secondary">
                          {group.members?.length || 0} members
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Mark as Paid Modal ────────────────────────────────────────────────── */}
      {showPayModal && payingCard && (
        <div className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4" onClick={() => setShowPayModal(false)}>
          <div className="bg-wa-panel rounded-xl max-w-sm w-full shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-5 py-3 bg-green-600 flex items-center justify-between text-white">
              <div>
                <h3 className="text-base font-bold">Mark as Paid?</h3>
                <p className="text-xs text-green-100">Amount: ₹ {payingCard.amount}</p>
              </div>
              <button onClick={() => setShowPayModal(false)} className="hover:bg-green-700 p-1 rounded transition-colors">
                <X size={20} weight="bold" />
              </button>
            </div>

            {/* Details Block */}
            <div className="px-5 py-3 border-b bg-wa-hover border-wa-border text-wa-text-primary">
              <div className="p-3 bg-wa-panel rounded-lg border border-wa-border text-[11px] font-mono leading-relaxed space-y-1">
                <div>User Name: {payingCard.username || '-'}</div>
                <div>A/c Holder Name: {payingCard.accountHolderName || '-'}</div>
                <div>Bank Account No: {payingCard.bankAccountNumber || '-'}</div>
                <div>IFSC Code: {payingCard.ifscCode || '-'}</div>
                <div>Bank Name: {payingCard.bankName || '-'}</div>
                {payingCard.upiId ? <div>UPI ID: {payingCard.upiId}</div> : null}
                <div>Amount: ₹ {payingCard.amount || '-'}</div>
              </div>
            </div>

            {/* Form */}
            <div className="px-5 py-4 space-y-3">
              <div>
                <label className="block text-[11px] font-semibold text-wa-text-secondary mb-1">UTR Number *</label>
                <input
                  type="text"
                  value={payUtr}
                  onChange={(e) => setPayUtr(e.target.value)}
                  placeholder="Enter UTR / Transaction ID"
                  className="w-full bg-wa-hover border border-wa-border rounded-lg px-3 py-2 text-[13px] text-wa-text-primary placeholder-wa-text-secondary focus:outline-none focus:ring-2 focus:ring-green-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-wa-text-secondary mb-1">Paid From (Account / UPI) (optional)</label>
                <input
                  type="text"
                  value={payPaidFrom}
                  onChange={(e) => setPayPaidFrom(e.target.value)}
                  placeholder="e.g. HDFC xxxx4567 or GPay"
                  className="w-full bg-wa-hover border border-wa-border rounded-lg px-3 py-2 text-[13px] text-wa-text-primary placeholder-wa-text-secondary focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-wa-text-secondary mb-1">Reference / Note (optional)</label>
                <input
                  type="text"
                  value={payReference}
                  onChange={(e) => setPayReference(e.target.value)}
                  placeholder="Any additional note"
                  className="w-full bg-wa-hover border border-wa-border rounded-lg px-3 py-2 text-[13px] text-wa-text-primary placeholder-wa-text-secondary focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="px-5 pb-5 flex gap-2">
              <button
                onClick={handleMarkAsPaid}
                disabled={submittingPay}
                className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold text-sm py-2.5 rounded-lg transition-colors flex items-center justify-center gap-1"
              >
                {submittingPay ? '⏳ Processing...' : '✓ Mark as Paid'}
              </button>
              <button
                onClick={handleCancelRequest}
                disabled={submittingPay}
                className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-semibold text-sm py-2.5 rounded-lg transition-colors"
              >
                ✕ Cancel Request
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ──────────────────────────────────────────────────────────────────────── */}

      {/* View Count Modal */}
      {showViewersModal && (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4" onClick={() => setShowViewersModal(false)}>
          <div className="bg-wa-panel rounded-lg max-w-sm w-full max-h-[60vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b flex justify-between items-center bg-wa-primary-strong text-white">
              <div className="flex items-center gap-2">
                <Eye size={24} weight="bold" />
                <h3 className="text-lg font-semibold">Message Views</h3>
              </div>
              <button onClick={() => setShowViewersModal(false)} className="hover:bg-green-700 p-1 rounded">
                <X size={24} weight="bold" />
              </button>
            </div>
            
            <div className="p-4 text-center border-b">
              <div className="text-3xl font-bold text-green-600">{viewersData.view_count}</div>
              <div className="text-sm text-wa-text-secondary">Total Views</div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4">
              {viewersData.viewers?.length === 0 ? (
                <div className="text-center text-wa-text-secondary py-4">
                  No views yet
                </div>
              ) : (
                <div className="space-y-3">
                  {viewersData.viewers?.map((viewer, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-wa-hover rounded-lg">
                      <span className="font-medium text-wa-text-primary">{viewer.name}</span>
                      <span className="text-xs text-wa-text-secondary">
                        {new Date(viewer.viewed_at).toLocaleString('en-IN', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}


      {/* Input Area - WhatsApp Style */}
      <div ref={inputContainerRef} className="flex-shrink-0">
        {/* Read-Only Banner for Customers in Group Chats */}
        {user?.role === 'customer' ? (
          <div className="fixed bottom-0 left-0 right-0 bg-wa-panel border-t p-3 z-50">
            <div className="bg-wa-hover border border-wa-border text-wa-text-secondary py-3 px-4 flex items-center justify-center gap-2 rounded-lg">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span className="text-sm font-medium">This is a broadcast channel - You can only view messages</span>
            </div>
          </div>
        ) : (
          <div className="fixed bottom-0 left-0 right-0 z-50">
            <WhatsAppInput
              onSendMessage={handleWhatsAppSendMessage}
              onSendMedia={handleGroupMediaSelected}
              onOpenMediaPicker={() => setShowMediaPicker(true)}
              placeholder="Type a message..."
              disabled={false}
              replyingTo={replyingTo}
              onCancelReply={cancelReply}
              userId={user?.id}
              userRole={user?.role || 'customer'}
            />
          </div>
        )}
      </div>

    </div>

    {/* Media Picker Modal for Group Chat */}
    {showMediaPicker && (
      <MediaPicker
        onMediaSelected={handleGroupMediaSelected}
        onClose={() => setShowMediaPicker(false)}
      />
    )}

    {/* Image Preview Modal - Outside main container */}
    {showImagePreview && previewImageUrl && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            closeImagePreview();
          }
        }}
      >
        <button 
          onClick={closeImagePreview}
          className="absolute top-4 right-4 text-white text-3xl font-bold hover:text-gray-300 z-60 w-10 h-10 flex items-center justify-center bg-black bg-opacity-50 rounded-full"
        >
          ×
        </button>
        <img 
          src={previewImageUrl} 
          alt="Preview" 
          className="max-w-full max-h-full object-contain cursor-zoom-out"
          onClick={closeImagePreview}
        />
      </div>
    )}
    </>
  );
};

export default GroupChatPage;
